import { Element } from "@xmpp/xml"
import MessageParser from "./messageParser"
import ReactionsParser from "./reactionsParser"
import { parseArchivedTimestamp, parseStanza } from "./stanzaParser"
import { ChatEvent } from "@/reducers/messagesSlice"

export type ForeignChatLastEvent = {
  /** ISO stamp the archive put on the result. */
  stamp?: string
  /** The event itself, when it is one the chat list knows how to preview. */
  event?: ChatEvent
}

/**
 * Turns one `urn:xmpp:mam:2` result into the last event of a foreign chat.
 *
 * Classification is left to `parseStanza`, the same one the room sync runs on
 * every archived stanza, so a foreign row reads its preview by the rules the
 * plain list already follows. Whatever it classifies as neither a message nor a
 * reaction — a marker, a retraction — leaves the stamp alone, so the row still
 * says when the room last moved.
 */
export const parseForeignChatLastEvent = (
  stanza: Element,
  myBareJid?: string,
): ForeignChatLastEvent => {
  if (!stanza.getChild("result")) return {}

  const stamp = parseArchivedTimestamp(stanza)
  const result = parseStanza(stanza, myBareJid)
  if (!result) return { stamp }

  const { stanza: messageStanza, type } = result

  if (type === "archived_message" || type === "ai_widget_map") {
    try {
      return {
        stamp,
        event: MessageParser.parse(messageStanza, { timestamp: stamp }),
      }
    } catch {
      // A groupchat message with no stanza-id cannot be identified, and an
      // unidentified message is not worth previewing.
      return { stamp }
    }
  }

  if (type === "archived_reaction") {
    return { stamp, event: ReactionsParser.parse(messageStanza, stamp) }
  }

  return { stamp }
}

export default parseForeignChatLastEvent
